import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Key, Plus, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";

const BTN = "border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 hover:bg-black hover:text-white disabled:opacity-40";
const METHODS = [["both", "Their system and their portal"], ["api", "Only their system"], ["portal", "Only by hand in their portal"]];
const errText = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);
const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

function ApiHelp({ s, newKey }) {
  const example = JSON.stringify({ lead_id: "the lead id we sent", status: "Retained", notes: "optional" }, null, 2);
  const keyText = newKey || (s.has_key ? `${s.key_prefix}… (create a new key to see it again)` : "create a key below");
  return (
    <div className="space-y-2 border border-black bg-zinc-50 p-3 text-[11px]" data-testid="status-api-help">
      <div className="flex flex-wrap items-center gap-2">
        <b>Send to:</b><span className="break-all font-mono" data-testid="status-api-url">POST {s.api_url}</span>
        <button type="button" className={BTN} onClick={() => copy(s.api_url)} aria-label="Copy the address" data-testid="status-api-url-copy"><Copy size={11} /></button>
      </div>
      <div><b>Header:</b> <span className="font-mono">X-Api-Key: {keyText}</span></div>
      <pre className="overflow-x-auto border border-zinc-300 bg-white p-2 font-mono text-[10.5px]">{example}</pre>
      <div>Status can be one of the statuses below or their own word. Words they use often are matched for you.</div>
    </div>
  );
}

function KeyBox({ orgId, s, newKey, onKey }) {
  const [busy, setBusy] = useState(false);
  const make = async () => {
    if (s.has_key && !window.confirm("Replace the key? The old key stops working right away.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${orgId}/status-key`);
      onKey(data);
    } catch (e) {
      toast.error(errText(e, "Couldn't create the key"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid="status-key-box">
      <span data-testid="status-key-state">{s.has_key ? `Key ${s.key_prefix}… made ${new Date(s.key_created_at).toLocaleDateString()}` : "No key yet."}</span>
      <button type="button" className={BTN} onClick={make} disabled={busy} data-testid="status-key-create"><Key size={11} /> {s.has_key ? "Replace key" : "Create key"}</button>
      {newKey && (
        <div className="w-full border-2 border-amber-600 bg-amber-50 p-2" data-testid="status-key-new">
          <div>Copy this key now and send it to the buyer&apos;s tech team. It won&apos;t be shown again.</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="break-all font-mono" data-testid="status-key-value">{newKey}</span>
            <button type="button" className={BTN} onClick={() => copy(newKey)} aria-label="Copy the key" data-testid="status-key-copy"><Copy size={11} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function WordMap({ rows, setRows, statuses }) {
  const update = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div data-testid="status-word-map">
      <div className="label-mono mb-1">Their words</div>
      <p className="mb-2 text-[11px] text-zinc-600">Common words like &ldquo;Retained&rdquo; or &ldquo;No answer&rdquo; are matched for you. Add any word their system uses that isn&apos;t.</p>
      {rows.map((r, i) => (
        <div key={i} className="mb-1.5 flex flex-wrap gap-2" data-testid={`status-word-row-${i}`}>
          <input value={r.word} onChange={(e) => update(i, { word: e.target.value })} placeholder="Their word, e.g. Retainer out"
                 className="min-w-0 flex-1 border border-black px-2 py-1 text-xs" data-testid={`status-word-${i}`} />
          <select value={r.status} onChange={(e) => update(i, { status: e.target.value })} className="border border-black px-2 py-1 text-xs" data-testid={`status-word-status-${i}`}>
            {statuses.filter((x) => x.key !== "other").map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
          <button type="button" className={BTN} onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove this word" data-testid={`status-word-remove-${i}`}><Trash size={11} /></button>
        </div>
      ))}
      <button type="button" className={BTN} onClick={() => setRows((rs) => [...rs, { word: "", status: "contacted" }])} data-testid="status-word-add"><Plus size={11} /> Add a word</button>
    </div>
  );
}

function Recent({ rows }) {
  if (!rows.length) return <div className="text-[11px] text-zinc-500" data-testid="status-recent-empty">No statuses sent back yet.</div>;
  return (
    <div className="border border-black" data-testid="status-recent">
      {rows.map((r) => (
        <div key={r.lead_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-200 px-2 py-1.5 text-[11px] last:border-b-0" data-testid={`status-recent-${r.lead_id}`}>
          <Link to={`/leads/${r.lead_id}`} className="font-semibold underline">{r.consumer_name || r.lead_id}</Link>
          <span className="border border-black px-1 uppercase tracking-widest text-[9px]">{r.label}</span>
          {r.raw && r.raw !== r.label && <span className="text-zinc-600">&ldquo;{r.raw}&rdquo;</span>}
          <span className="font-mono text-[10px] text-zinc-500">{r.source === "api" ? "their system" : "portal"} · {new Date(r.at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// Admin set-up for how one buyer sends lead statuses back.
export function LeadStatusSettings({ orgId }) {
  const [s, setS] = useState(null);
  const [method, setMethod] = useState("both");
  const [rows, setRows] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const apply = useCallback((d) => {
    setS(d);
    setMethod(d.method);
    setRows(Object.entries(d.word_map || {}).map(([word, status]) => ({ word, status })));
  }, []);
  useEffect(() => {
    api.get(`/admin/clients/${orgId}/status-settings`).then((r) => apply(r.data)).catch((e) => toast.error(errText(e, "Couldn't load status settings")));
  }, [orgId, apply]);
  if (!s) return null;
  const save = async () => {
    setBusy(true);
    try {
      const word_map = Object.fromEntries(rows.filter((r) => r.word.trim()).map((r) => [r.word.trim(), r.status]));
      apply((await api.put(`/admin/clients/${orgId}/status-settings`, { method, word_map })).data);
      toast.success("Status settings saved");
    } catch (e) {
      toast.error(errText(e, "Couldn't save"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="lead-status-settings">
      <p className="text-[11px] text-zinc-600">The buyer tells us what happened with each lead they got. Statuses show on the lead and in their portal.</p>
      <label className="block max-w-sm">
        <span className="label-mono">How they send statuses</span>
        <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 w-full border border-black px-2 py-1.5 text-sm" data-testid="status-method">
          {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      {method !== "portal" && (
        <>
          <KeyBox orgId={orgId} s={s} newKey={newKey} onKey={(d) => { setNewKey(d.key); setS((x) => ({ ...x, has_key: true, key_prefix: d.key_prefix, key_created_at: new Date().toISOString() })); }} />
          <ApiHelp s={s} newKey={newKey} />
        </>
      )}
      <div className="flex flex-wrap gap-1.5" data-testid="status-standard-list">
        {s.statuses.map((x) => <span key={x.key} className="border border-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest">{x.label}</span>)}
      </div>
      <WordMap rows={rows} setRows={setRows} statuses={s.statuses} />
      <button type="button" onClick={save} disabled={busy} className={`${BTN} bg-black text-white`} data-testid="status-settings-save">{busy ? "Saving…" : "Save"}</button>
      <div>
        <div className="label-mono mb-1">Latest statuses</div>
        <Recent rows={s.recent} />
      </div>
    </div>
  );
}
